import axios from 'axios';
import { CRYPTO_FIAT_PAIRS, TRADING_PAIRS } from '@/utils/currencies';

const COINBASE_API_BASE = 'https://api.coinbase.com/v2/prices';

/**
 * Fetch price data from Coinbase API
 * @param {string} ticker - e.g., 'usdt-cny'
 * @param {string} type - 'buy' or 'sell'
 * @returns {Promise<number>} - price value
 */
const fetchCoinbasePrice = async (ticker, type) => {
  try {
    const response = await axios.get(`${COINBASE_API_BASE}/${ticker}/${type}`);
    return parseFloat(response.data.data.amount);
  } catch (error) {
    console.error(`Error fetching ${ticker} ${type} price:`, error);
    return null;
  }
};

/**
 * Calculate custom price with margin for specific pairs
 * @param {string} ticker
 * @param {string} side - 'buy' or 'sell'
 * @param {object} rates - current rates object
 * @returns {number|string} - calculated price or '--'
 */
const calculateCustomPrice = (ticker, side, rates) => {
  try {
    // Special handling for AUDD pairs (hardcoded rates from original)
    if (ticker === 'audd-aud') {
      return side === 'sell' ? 0.9754 : 1.0253;
    }

    const audCnyBuyPrice = +(rates['aud-cny']?.buyPrice || 0);
    const usdtCnyBuyPrice = +(rates['usdt-cny']?.buyPrice || 0);
    const audUsdtBuyPrice = +(rates['aud-usdt']?.buyPrice || 0);

    if (ticker === 'audd-usdt') {
      return side === 'buy'
        ? (1 / ((1 / audUsdtBuyPrice) * 0.9981 * 0.9754)).toFixed(4)
        : (
            (1 * 0.9754 * audCnyBuyPrice) /
            1.02 /
            (usdtCnyBuyPrice * 1.035)
          ).toFixed(4);
    }

    // Add AUDD/USD
    if (ticker === 'audd-usd') {
      const audUsdBuyPrice = +(rates['aud-usd']?.buyPrice || 0);
      return side === 'buy'
        ? (audUsdBuyPrice * 0.9981 * 0.9754).toFixed(4)
        : ((audUsdBuyPrice * 0.9754) / 1.02).toFixed(4);
    }

    // Add AUDD/SGD
    if (ticker === 'audd-sgd') {
      const audSgdBuyPrice = +(rates['aud-sgd']?.buyPrice || 0);
      return side === 'buy'
        ? (audSgdBuyPrice * 0.9981 * 0.9754).toFixed(4)
        : ((audSgdBuyPrice * 0.9754) / 1.02).toFixed(4);
    }

    // Add AUDD/HKD
    if (ticker === 'audd-hkd') {
      const audHkdBuyPrice = +(rates['aud-hkd']?.buyPrice || 0);
      return side === 'buy'
        ? (audHkdBuyPrice * 0.9981 * 0.9754).toFixed(4)
        : ((audHkdBuyPrice * 0.9754) / 1.02).toFixed(4);
    }

    // Add AUDD/MYR
    if (ticker === 'audd-myr') {
      const audMyrBuyPrice = +(rates['aud-myr']?.buyPrice || 0);
      return side === 'buy'
        ? (audMyrBuyPrice * 0.9981 * 0.9754).toFixed(4)
        : ((audMyrBuyPrice * 0.9754) / 1.02).toFixed(4);
    }

    // Add AUDD/USDC
    if (ticker === 'audd-usdc') {
      const audUsdcBuyPrice = +(rates['aud-usdc']?.buyPrice || 0);
      return side === 'buy'
        ? (audUsdcBuyPrice * 0.9981 * 0.9754).toFixed(4)
        : ((audUsdcBuyPrice * 0.9754) / 1.02).toFixed(4);
    }

    // Add BTC/AUDD
    if (ticker === 'btc-audd') {
      const btcAudBuyPrice = +(rates['btc-aud']?.buyPrice || 0);
      return side === 'buy'
        ? (btcAudBuyPrice / 0.9754).toFixed(4)
        : (btcAudBuyPrice * 0.9754).toFixed(4);
    }

    // Add ETH/AUDD
    if (ticker === 'eth-audd') {
      const ethAudBuyPrice = +(rates['eth-aud']?.buyPrice || 0);
      return side === 'buy'
        ? (ethAudBuyPrice / 0.9754).toFixed(4)
        : (ethAudBuyPrice * 0.9754).toFixed(4);
    }

    if (ticker === 'audd-cny') {
      return side === 'buy'
        ? ((audCnyBuyPrice * 1.048) / 0.9754).toFixed(4)
        : ((audCnyBuyPrice / 1.02) * 0.9754).toFixed(4);
    }

    if (ticker === 'usdt-cny') {
      return side === 'buy'
        ? (+usdtCnyBuyPrice * 1.048).toFixed(4)
        : usdtCnyBuyPrice.toFixed(4);
    }

    if (ticker === 'aud-usdt') {
      return side === 'buy'
        ? (+audUsdtBuyPrice * 1.03714).toFixed(4)
        : (+audUsdtBuyPrice * (1 - 0.0322)).toFixed(4);
    }

    // Standard margin calculation for other pairs
    const sidePrice = `${side}Price`;
    const config = TRADING_PAIRS[ticker];
    const margin = config?.margin || 0.0264;

    if (ticker in rates && rates[ticker][sidePrice]) {
      const basePrice = +rates[ticker][sidePrice];
      const adjustedPrice =
        basePrice * (side === 'buy' ? 1 + margin : 1 - margin);
      return adjustedPrice.toFixed(config?.priceAccuracy || 4);
    }

    return '--';
  } catch (error) {
    console.error('calculateCustomPrice error:', error);
    return '--';
  }
};

/**
 * Fetch all rates for supported pairs
 * @returns {Promise<object>} - rates object
 */
export const fetchAllRates = async () => {
  const rates = {};
  // Collect all pairs to fetch: display pairs + their dependencies
  const tickersToFetch = new Set();

  // Add all display pairs
  CRYPTO_FIAT_PAIRS.forEach(pair => tickersToFetch.add(pair));

  // Add all dependencies from TRADING_PAIRS
  Object.values(TRADING_PAIRS).forEach(config => {
    if (config.dependencies) {
      config.dependencies.forEach(dep => tickersToFetch.add(dep));
    }
  });

  // Also add all direct trading pairs (for completeness)
  Object.keys(TRADING_PAIRS).forEach(pair => tickersToFetch.add(pair));

  const promises = [];
  Array.from(tickersToFetch).forEach(ticker => {
    if (!ticker.includes('audd')) {
      promises.push(
        fetchCoinbasePrice(ticker, 'buy').then(price => {
          if (price !== null) {
            if (!rates[ticker]) rates[ticker] = {};
            rates[ticker].buyPrice = price;
          }
        }),
      );
      promises.push(
        fetchCoinbasePrice(ticker, 'sell').then(price => {
          if (price !== null) {
            if (!rates[ticker]) rates[ticker] = {};
            rates[ticker].sellPrice = price;
          }
        }),
      );
    }
  });

  await Promise.all(promises);
  return rates;
};

/**
 * Get display price for a ticker and side
 * @param {string} ticker
 * @param {string} side - 'buy' or 'sell'
 * @param {object} rates - current rates
 * @returns {string} - formatted price
 */
export const getDisplayPrice = (ticker, side, rates) => {
  return calculateCustomPrice(ticker, side, rates);
};

/**
 * Price service class for managing real-time price updates
 */
export class PriceService {
  constructor() {
    this.rates = {};
    this.subscribers = [];
    this.updateInterval = null;
    this.isRunning = false;
  }

  /**
   * Subscribe to price updates
   * @param {function} callback - called when prices update
   * @returns {function} - unsubscribe function
   */
  subscribe(callback) {
    this.subscribers.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }

  /**
   * Notify all subscribers of price updates
   */
  notifySubscribers() {
    this.subscribers.forEach(callback => {
      try {
        callback(this.rates);
      } catch (error) {
        console.error('Error in price subscriber:', error);
      }
    });
  }

  /**
   * Start automatic price updates
   * @param {number} intervalMs - update interval in milliseconds (default: 30000)
   */
  start(intervalMs = 30000) {
    if (this.isRunning) return;

    this.isRunning = true;

    // Initial fetch
    this.updateRates();

    // Set up interval
    this.updateInterval = setInterval(() => {
      this.updateRates();
    }, intervalMs);
  }

  /**
   * Stop automatic price updates
   */
  stop() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    this.isRunning = false;
  }

  /**
   * Manually update rates
   */
  async updateRates() {
    try {
      const newRates = await fetchAllRates();
      this.rates = { ...this.rates, ...newRates };
      this.notifySubscribers();
    } catch (error) {
      console.error('Error updating rates:', error);
    }
  }

  /**
   * Get current rates
   */
  getRates() {
    return this.rates;
  }
}

// Export singleton instance
export const priceService = new PriceService();
